import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { CURRENCY_OPTIONS, currencySymbol } from '../utils/currency';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * Compact navbar currency picker. Click opens a small popover with the six
 * supported currencies; selection is saved to localStorage and (for logged-in
 * users) persisted to the account via /api/profile.
 *
 * Props:
 *   variant — 'navbar' (small, matches the top bar) or 'profile' (block select)
 */
export default function CurrencyPicker({ variant = 'navbar', className = '' }) {
    const { preferred, setPreferred } = useCurrency();
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [open]);

    if (variant === 'profile') {
        return (
            <select
                value={preferred}
                onChange={(e) => setPreferred(e.target.value)}
                className={`bg-white border border-[#E5E0D8] px-3 py-2 rounded-sm text-sm ${className}`}
                data-testid="currency-picker-select"
            >
                {CURRENCY_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                ))}
            </select>
        );
    }

    const activeMeta = CURRENCY_OPTIONS.find(c => c.value === preferred) || CURRENCY_OPTIONS[0];

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-sm px-2.5 py-1.5 border border-[#E5E0D8] rounded-sm hover:bg-[#F9F6F0] text-[#1A1A2E]"
                aria-haspopup="menu"
                aria-expanded={open}
                data-testid="nav-currency-picker"
                title={`Currency: ${activeMeta.code}`}
            >
                <span className="font-medium">{currencySymbol(preferred)}</span>
                <span className="hidden md:inline text-[11px] uppercase tracking-widest text-[#4A4A68]">
                    {activeMeta.code}
                </span>
                <ChevronDown className="w-3 h-3 text-[#4A4A68]" />
            </button>

            {open && (
                <div
                    className="absolute right-0 z-50 mt-2 w-56 bg-white border border-[#E5E0D8] rounded-sm shadow-lg py-1"
                    role="menu"
                    data-testid="nav-currency-menu"
                >
                    {CURRENCY_OPTIONS.map(c => {
                        const active = c.value === preferred;
                        return (
                            <button
                                key={c.value}
                                type="button"
                                onClick={() => { setPreferred(c.value); setOpen(false); }}
                                className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left ${active ? 'bg-[#F9F6F0]' : 'hover:bg-[#F9F6F0]'}`}
                                role="menuitem"
                                data-testid={`nav-currency-option-${c.value}`}
                            >
                                <span className="w-5 inline-flex justify-center text-[#5B2D8E]">
                                    {active ? <Check className="w-4 h-4" /> : <span className="opacity-0">•</span>}
                                </span>
                                <span className="flex-1">{c.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
